import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  cloneClientViewLaunch,
  completeLaunchReview,
  createClientViewFromTemplate,
  downloadClientViewBundle,
  getLaunchChecklist,
  importManifestBundle,
  listLaunchClientViews,
  listLaunchTemplates,
  runLaunchChecklist,
  validateManifestImport,
} from "../../api/adminClientViewLaunchApi";
import RequirePermission from "../../auth/RequirePermission";
import { useAuth } from "../../auth/useAuth";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";
import "../../styles/clientViewStudio.css";

const MODES = {
  DASHBOARD: "dashboard",
  TEMPLATE: "template",
  CLONE: "clone",
  IMPORT: "import",
  CHECKLIST: "checklist",
};

const WIZARD_STEPS = [
  "Choose template",
  "Basic details",
  "Branding",
  "Review",
  "Create",
];

function ReadinessCard({ readiness }) {
  if (!readiness) return null;
  const tone =
    readiness.score >= 90 ? "success" : readiness.score >= 70 ? "warning" : "danger";
  return (
    <div className="panel-card launch-readiness-card">
      <div className="launch-readiness-score">
        <span className={`launch-score launch-score--${tone}`}>{readiness.score}</span>
        <div>
          <h3>{readiness.label}</h3>
          <p className="muted">{readiness.blockers?.length || 0} required blocker(s)</p>
        </div>
      </div>
      {readiness.blockers?.length ? (
        <ul className="launch-blockers-list">
          {readiness.blockers.map((item) => (
            <li key={item.itemKey}>
              <strong>{item.label}</strong> — {item.resultMessage}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ClientViewLaunchPageContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("client_views.manage");
  const canPublish = hasPermission("client_views.publish");

  const mode = searchParams.get("mode") || MODES.DASHBOARD;
  const selectedViewId = searchParams.get("clientViewId") || "";

  const [templates, setTemplates] = useState([]);
  const [clientViews, setClientViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [wizardStep, setWizardStep] = useState(0);
  const [templateForm, setTemplateForm] = useState({
    templateKey: "blank-client-view",
    name: "",
    slug: "",
    brandName: "",
    description: "",
    primaryColor: "#1e3a5f",
  });

  const [cloneForm, setCloneForm] = useState({
    sourceClientViewId: "",
    sourceVersion: "published",
    name: "",
    slug: "",
    copyBranding: true,
    copyPages: true,
    copyQueryProfiles: true,
    copySponsorshipInventory: false,
    copyDomains: false,
  });

  const [importJson, setImportJson] = useState("");
  const [importValidation, setImportValidation] = useState(null);
  const [importName, setImportName] = useState("");
  const [importSlug, setImportSlug] = useState("");

  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const setMode = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set("mode", next);
    setSearchParams(params);
  };

  const loadBase = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [tplBody, viewsBody] = await Promise.all([
        listLaunchTemplates(),
        listLaunchClientViews(),
      ]);
      setTemplates(tplBody.templates || []);
      setClientViews(viewsBody.clientViews || []);
    } catch (err) {
      setError(err.message || "Unable to load launch toolkit.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChecklist = useCallback(async (clientViewId) => {
    if (!clientViewId) return;
    try {
      setChecklistLoading(true);
      const body = await getLaunchChecklist(clientViewId);
      setChecklist(body);
    } catch (err) {
      setError(err.message || "Unable to load checklist.");
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (mode === MODES.CHECKLIST && selectedViewId) {
      loadChecklist(selectedViewId);
    }
  }, [mode, selectedViewId, loadChecklist]);

  const handleCreateFromTemplate = async () => {
    if (!canManage) return;
    setError("");
    setSuccess("");
    try {
      const body = await createClientViewFromTemplate({
        templateKey: templateForm.templateKey,
        name: templateForm.name,
        slug: templateForm.slug || undefined,
        brandName: templateForm.brandName || templateForm.name,
        description: templateForm.description,
        branding: {
          colors: { primary: templateForm.primaryColor, accent: "#2563eb" },
        },
      });
      setSuccess(`Created draft “${body.clientView.name}”.`);
      navigate(`/admin/client-views/${body.clientView.clientViewId}/studio`);
    } catch (err) {
      setError(err.message || "Create from template failed.");
    }
  };

  const handleClone = async () => {
    if (!canManage) return;
    setError("");
    try {
      const body = await cloneClientViewLaunch(cloneForm);
      setSuccess(`Cloned to “${body.clientView.name}”.`);
      navigate(`/admin/client-views/${body.clientView.clientViewId}/studio`);
    } catch (err) {
      setError(err.message || "Clone failed.");
    }
  };

  const handleValidateImport = async () => {
    setError("");
    try {
      let bundle;
      try {
        bundle = JSON.parse(importJson);
      } catch {
        setError("Invalid JSON syntax.");
        return;
      }
      const body = await validateManifestImport({ bundle });
      setImportValidation(body);
    } catch (err) {
      setError(err.message || "Validation failed.");
    }
  };

  const handleImport = async () => {
    if (!canManage) return;
    setError("");
    try {
      const bundle = JSON.parse(importJson);
      const body = await importManifestBundle({
        bundle,
        name: importName || undefined,
        slug: importSlug || undefined,
        mode: "create",
      });
      setSuccess(`Imported draft “${body.clientView.name}”.`);
      navigate(`/admin/client-views/${body.clientView.clientViewId}/studio`);
    } catch (err) {
      setError(err.message || "Import failed.");
    }
  };

  const handleRunChecklist = async () => {
    if (!selectedViewId || !canManage) return;
    try {
      const body = await runLaunchChecklist(selectedViewId);
      setChecklist(body);
      setSuccess("Checklist updated.");
    } catch (err) {
      setError(err.message || "Checklist run failed.");
    }
  };

  const handleLaunchReview = async () => {
    if (!selectedViewId || !canManage) return;
    try {
      const body = await completeLaunchReview(selectedViewId, {});
      setChecklist((prev) => ({ ...prev, readiness: body.readiness, grouped: body.grouped }));
      setSuccess(body.recommendPublish ? "Ready to launch." : "Launch blocked — fix required items.");
    } catch (err) {
      setError(err.message || "Launch review failed.");
    }
  };

  const handleExport = async (clientViewId) => {
    try {
      const blob = await downloadClientViewBundle(clientViewId, "draft");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client-view-${clientViewId}-bundle.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Export failed.");
    }
  };

  const selectedTemplate = templates.find((t) => t.templateKey === templateForm.templateKey);

  return (
    <div className="client-view-studio launch-toolkit-page">
      <header className="studio-header panel-card">
        <div>
          <p className="eyebrow">Client Views</p>
          <h1>Launch Toolkit</h1>
          <p className="muted">
            Create, clone, import, and validate Client Views before publish — no custom development
            required.
          </p>
        </div>
        <Link to="/" className="midpoint-share-button">
          Back to admin
        </Link>
      </header>

      <nav className="studio-tabs launch-toolkit-tabs" aria-label="Launch toolkit sections">
        {Object.entries(MODES).map(([key, value]) => (
          <button
            key={value}
            type="button"
            className={`studio-tab${mode === value ? " is-active" : ""}`}
            onClick={() => setMode(value)}
          >
            {key.charAt(0) + key.slice(1).toLowerCase()}
          </button>
        ))}
      </nav>

      {error ? <p className="admin-data-health-error panel-card">{error}</p> : null}
      {success ? <p className="panel-card launch-success-msg">{success}</p> : null}

      {mode === MODES.DASHBOARD ? (
        <section className="panel-card">
          <h2>Launch dashboard</h2>
          {loading ? <p className="muted">Loading…</p> : null}
          <div className="launch-dashboard-actions">
            {canManage ? (
              <>
                <button type="button" className="report-submit-cta" onClick={() => setMode(MODES.TEMPLATE)}>
                  Create from template
                </button>
                <button type="button" className="midpoint-share-button" onClick={() => setMode(MODES.CLONE)}>
                  Clone existing view
                </button>
                <button type="button" className="midpoint-share-button" onClick={() => setMode(MODES.IMPORT)}>
                  Import manifest
                </button>
              </>
            ) : null}
          </div>
          <h3>Your Client Views</h3>
          <ul className="launch-view-list">
            {clientViews.map((view) => (
              <li key={view.clientViewId} className="launch-view-list-item">
                <div>
                  <strong>{view.name}</strong>
                  <span className="muted"> /v/{view.slug}</span>
                  <StatusBadge tone={view.status === "active" ? "success" : "neutral"} label={view.status} />
                </div>
                <div className="launch-view-actions">
                  <button
                    type="button"
                    className="midpoint-share-button"
                    onClick={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set("mode", MODES.CHECKLIST);
                      params.set("clientViewId", view.clientViewId);
                      setSearchParams(params);
                    }}
                  >
                    Checklist
                  </button>
                  <Link
                    to={`/admin/client-views/${view.clientViewId}/studio`}
                    className="midpoint-share-button"
                  >
                    Studio
                  </Link>
                  {canManage ? (
                    <button
                      type="button"
                      className="midpoint-share-button"
                      onClick={() => handleExport(view.clientViewId)}
                    >
                      Export
                    </button>
                  ) : null}
                  <a
                    href={`/v/${view.slug}?preview=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="midpoint-share-button"
                  >
                    Preview
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {mode === MODES.TEMPLATE && canManage ? (
        <section className="panel-card">
          <h2>Create from template</h2>
          <div className="launch-wizard-steps">
            {WIZARD_STEPS.map((label, index) => (
              <span
                key={label}
                className={`launch-wizard-step${wizardStep === index ? " is-active" : ""}`}
              >
                {index + 1}. {label}
              </span>
            ))}
          </div>

          {wizardStep === 0 ? (
            <div className="launch-template-grid">
              {templates.map((tpl) => (
                <button
                  key={tpl.templateKey}
                  type="button"
                  className={`launch-template-card${
                    templateForm.templateKey === tpl.templateKey ? " is-selected" : ""
                  }`}
                  onClick={() =>
                    setTemplateForm((f) => ({
                      ...f,
                      templateKey: tpl.templateKey,
                      name: f.name || tpl.displayName,
                    }))
                  }
                >
                  <span className="launch-template-icon">{tpl.icon || "📄"}</span>
                  <strong>{tpl.displayName}</strong>
                  <p className="muted">{tpl.description}</p>
                </button>
              ))}
              <button type="button" className="report-submit-cta" onClick={() => setWizardStep(1)}>
                Continue
              </button>
            </div>
          ) : null}

          {wizardStep === 1 ? (
            <div className="launch-form-grid">
              <label>
                View name
                <input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label>
                Slug (optional — auto-generated)
                <input
                  value={templateForm.slug}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, slug: e.target.value }))}
                />
              </label>
              <label>
                Brand name
                <input
                  value={templateForm.brandName}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, brandName: e.target.value }))}
                />
              </label>
              <label>
                Description
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <div className="launch-form-actions">
                <button type="button" className="midpoint-share-button" onClick={() => setWizardStep(0)}>
                  Back
                </button>
                <button type="button" className="report-submit-cta" onClick={() => setWizardStep(2)}>
                  Continue
                </button>
              </div>
            </div>
          ) : null}

          {wizardStep === 2 ? (
            <div className="launch-form-grid">
              <label>
                Primary color
                <input
                  type="color"
                  value={templateForm.primaryColor}
                  onChange={(e) => setTemplateForm((f) => ({ ...f, primaryColor: e.target.value }))}
                />
              </label>
              {selectedTemplate ? (
                <div>
                  <h4>Template includes</h4>
                  <ul>
                    {selectedTemplate.requiredSetupSteps?.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="launch-form-actions">
                <button type="button" className="midpoint-share-button" onClick={() => setWizardStep(1)}>
                  Back
                </button>
                <button type="button" className="report-submit-cta" onClick={() => setWizardStep(3)}>
                  Review
                </button>
              </div>
            </div>
          ) : null}

          {wizardStep === 3 ? (
            <div className="panel-card">
              <h3>Review</h3>
              <p>
                Template: <strong>{selectedTemplate?.displayName}</strong>
              </p>
              <p>
                Name: <strong>{templateForm.name}</strong>
              </p>
              <p className="muted">Creates a draft Client View — not auto-published.</p>
              <div className="launch-form-actions">
                <button type="button" className="midpoint-share-button" onClick={() => setWizardStep(2)}>
                  Back
                </button>
                <button type="button" className="report-submit-cta" onClick={handleCreateFromTemplate}>
                  Create draft
                </button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {mode === MODES.CLONE && canManage ? (
        <section className="panel-card">
          <h2>Clone Client View</h2>
          <div className="launch-form-grid">
            <label>
              Source view
              <select
                value={cloneForm.sourceClientViewId}
                onChange={(e) =>
                  setCloneForm((f) => ({ ...f, sourceClientViewId: e.target.value }))
                }
              >
                <option value="">Select…</option>
                {clientViews.map((v) => (
                  <option key={v.clientViewId} value={v.clientViewId}>
                    {v.name} ({v.slug})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Source version
              <select
                value={cloneForm.sourceVersion}
                onChange={(e) => setCloneForm((f) => ({ ...f, sourceVersion: e.target.value }))}
              >
                <option value="published">Published</option>
                <option value="draft">Current draft</option>
              </select>
            </label>
            <label>
              New name
              <input
                value={cloneForm.name}
                onChange={(e) => setCloneForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label>
              New slug
              <input
                value={cloneForm.slug}
                onChange={(e) => setCloneForm((f) => ({ ...f, slug: e.target.value }))}
              />
            </label>
            <fieldset>
              <legend>Copy options</legend>
              {[
                ["copyBranding", "Branding"],
                ["copyPages", "Pages"],
                ["copyQueryProfiles", "Query profiles"],
                ["copySponsorshipInventory", "Sponsorship inventory slots"],
                ["copyDomains", "Custom domains (not recommended)"],
              ].map(([key, label]) => (
                <label key={key} className="launch-checkbox">
                  <input
                    type="checkbox"
                    checked={cloneForm[key]}
                    onChange={(e) => setCloneForm((f) => ({ ...f, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <p className="muted">
              Leads, analytics, users, invoices, and API keys are never copied.
            </p>
            <button type="button" className="report-submit-cta" onClick={handleClone}>
              Create clone draft
            </button>
          </div>
        </section>
      ) : null}

      {mode === MODES.IMPORT && canManage ? (
        <section className="panel-card">
          <h2>Import manifest bundle</h2>
          <label>
            Paste launch bundle JSON
            <textarea
              rows={12}
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{ "bundleVersion": 1, "manifest": { ... } }'
            />
          </label>
          <div className="launch-form-actions">
            <button type="button" className="midpoint-share-button" onClick={handleValidateImport}>
              Validate
            </button>
          </div>
          {importValidation ? (
            <div className={`launch-validation${importValidation.valid ? " is-valid" : " is-invalid"}`}>
              <p>{importValidation.valid ? "Valid bundle" : "Validation errors"}</p>
              {importValidation.summary ? (
                <ul>
                  <li>Pages: {importValidation.summary.pageCount}</li>
                  <li>Data sources: {importValidation.summary.dataSourceCount}</li>
                  <li>Query profiles: {importValidation.summary.queryProfileCount}</li>
                </ul>
              ) : null}
              {importValidation.errors?.map((err) => (
                <p key={err} className="admin-data-health-error">
                  {err}
                </p>
              ))}
              {importValidation.warnings?.map((w) => (
                <p key={w} className="muted">
                  {w}
                </p>
              ))}
            </div>
          ) : null}
          {importValidation?.valid ? (
            <div className="launch-form-grid">
              <label>
                New view name
                <input value={importName} onChange={(e) => setImportName(e.target.value)} />
              </label>
              <label>
                Slug (optional)
                <input value={importSlug} onChange={(e) => setImportSlug(e.target.value)} />
              </label>
              <button type="button" className="report-submit-cta" onClick={handleImport}>
                Import as new draft
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {mode === MODES.CHECKLIST ? (
        <section className="panel-card">
          <h2>Launch checklist</h2>
          <label>
            Client View
            <select
              value={selectedViewId}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams);
                params.set("clientViewId", e.target.value);
                setSearchParams(params);
              }}
            >
              <option value="">Select…</option>
              {clientViews.map((v) => (
                <option key={v.clientViewId} value={v.clientViewId}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
          {selectedViewId ? (
            <>
              <ReadinessCard readiness={checklist?.readiness} />
              {canManage ? (
                <div className="launch-form-actions">
                  <button
                    type="button"
                    className="report-submit-cta"
                    onClick={handleRunChecklist}
                    disabled={checklistLoading}
                  >
                    Run checks
                  </button>
                  <button type="button" className="midpoint-share-button" onClick={handleLaunchReview}>
                    Complete launch review
                  </button>
                  <Link
                    to={`/admin/client-views/${selectedViewId}/studio?tab=publish`}
                    className="midpoint-share-button"
                  >
                    {canPublish ? "Go to publish" : "Open Studio"}
                  </Link>
                </div>
              ) : null}
              {checklistLoading ? <p className="muted">Loading checklist…</p> : null}
              {checklist?.grouped
                ? Object.entries(checklist.grouped).map(([category, items]) => (
                    <div key={category} className="launch-checklist-category">
                      <h3>{category}</h3>
                      <ul>
                        {items.map((item) => (
                          <li key={item.itemKey} className={`launch-check-item launch-check-item--${item.status}`}>
                            <StatusBadge
                              tone={
                                item.status === "passed"
                                  ? "success"
                                  : item.status === "failed"
                                    ? "danger"
                                    : "warning"
                              }
                              label={item.status}
                            />
                            <strong>{item.label}</strong>
                            <span className="muted"> ({item.severity})</span>
                            {item.resultMessage ? <p>{item.resultMessage}</p> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                : null}
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default function ClientViewLaunchPage() {
  return (
    <RequirePermission permission="client_views.view">
      <ClientViewLaunchPageContent />
    </RequirePermission>
  );
}
